
import { useState } from 'react';
import { projectsData, projectStats } from '../../../mocks/projects';

const filters = ['Todos', 'Residencial', 'Industrial', 'Finca'];

export default function ProjectsGallery() {
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [selectedProject, setSelectedProject] = useState<typeof projectsData[0] | null>(null);

  const filteredProjects = activeFilter === 'Todos'
    ? projectsData
    : projectsData.filter(p => p.type === activeFilter);

  return (
    <>
      <section id="galeria-proyectos" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <span className="inline-block bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">
                Proyectos Realizados
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Galería de Instalaciones
              </h2>
              <p className="text-gray-600 text-base mt-3 max-w-lg">
                Descubre nuestros proyectos fotovoltaicos completados con datos reales de ahorro energético y reducción de emisiones de CO₂
              </p>
            </div>
            <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-full self-start">
              {filters.map(f => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeFilter === f
                      ? 'bg-[#0A2E1F] text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="bg-solar-beige rounded-2xl p-5 text-center">
              <div className="text-3xl font-bold text-[#0A2E1F] mb-1">+{projectStats.totalProjects}</div>
              <div className="text-sm text-gray-600">Proyectos completados</div>
            </div>
            <div className="bg-solar-beige rounded-2xl p-5 text-center">
              <div className="text-3xl font-bold text-[#0A2E1F] mb-1">+{(projectStats.totalPanels / 1000).toFixed(0)}k</div>
              <div className="text-sm text-gray-600">Paneles instalados</div>
            </div>
            <div className="bg-solar-beige rounded-2xl p-5 text-center">
              <div className="text-3xl font-bold text-[#FF6B35] mb-1">{projectStats.totalSavings}M€</div>
              <div className="text-sm text-gray-600">Ahorro generado</div>
            </div>
            <div className="bg-solar-beige rounded-2xl p-5 text-center">
              <div className="text-3xl font-bold text-[#0A2E1F] mb-1">{(projectStats.totalCO2 / 1000).toFixed(0)}k t</div>
              <div className="text-sm text-gray-600">CO₂ evitado</div>
            </div>
          </div>

          {/* Projects Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map(project => (
              <article
                key={project.id}
                className="group relative rounded-2xl overflow-hidden cursor-pointer h-[480px] shadow-lg shadow-black/5 hover:-translate-y-2 transition-all duration-500"
                onClick={() => setSelectedProject(project)}
              >
                <img
                  src={project.image}
                  alt={`Proyecto solar ${project.title} en ${project.location}`}
                  title={`Instalación fotovoltaica ${project.title} - ${project.power}`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent"></div>

                {/* Savings Badge */}
                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-md">
                  <span className="text-[#0A2E1F] font-bold text-sm">Ahorro: {project.savingsPercent}%</span>
                </div>

                {/* Type Badge */}
                <div className="absolute top-4 left-4 bg-[#FFB800] px-3 py-1 rounded-full">
                  <span className="text-gray-900 font-semibold text-xs">{project.type}</span>
                </div>

                {/* Info */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-bold text-white mb-1">{project.title}</h3>
                  <div className="flex items-center gap-1.5 text-white/80 text-sm mb-4">
                    <i className="ri-map-pin-line"></i>
                    <span>{project.location}</span>
                    <span className="mx-1">·</span>
                    <span>{project.completionDate}</span>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white/15 backdrop-blur-sm rounded-lg px-3 py-2 text-center">
                      <div className="text-white font-bold text-sm">{project.panels}</div>
                      <div className="text-white/70 text-xs">Paneles</div>
                    </div>
                    <div className="bg-white/15 backdrop-blur-sm rounded-lg px-3 py-2 text-center">
                      <div className="text-[#FFB800] font-bold text-sm">{project.savingsYear.toLocaleString('es-ES')}€</div>
                      <div className="text-white/70 text-xs">Ahorro/año</div>
                    </div>
                    <div className="bg-white/15 backdrop-blur-sm rounded-lg px-3 py-2 text-center">
                      <div className="text-white font-bold text-sm">{project.co2Reduction}t</div>
                      <div className="text-white/70 text-xs">CO₂ evitado</div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-14">
            <a
              href="#presupuesto"
              className="inline-flex items-center gap-2 bg-[#0A2E1F] text-white px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-[#FFB800] hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              ¿Quieres un proyecto así?
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className="bg-white rounded-3xl overflow-hidden max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Image */}
            <div className="relative h-72 md:h-80">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-900"></i>
              </button>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-[#FFB800] px-3 py-1 rounded-full text-xs font-semibold text-gray-900">{selectedProject.type}</span>
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">{selectedProject.completionDate}</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white">{selectedProject.title}</h3>
                <div className="flex items-center gap-1.5 text-white/80 text-sm mt-1">
                  <i className="ri-map-pin-line"></i>
                  <span>{selectedProject.location}, España</span>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 md:p-8">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-solar-beige rounded-xl p-4 text-center">
                  <div className="w-10 h-10 bg-[#0A2E1F] rounded-lg flex items-center justify-center mx-auto mb-2">
                    <i className="ri-layout-grid-line text-[#FFB800]"></i>
                  </div>
                  <div className="text-xl font-bold text-gray-900">{selectedProject.panels}</div>
                  <div className="text-xs text-gray-500">Paneles</div>
                </div>
                <div className="bg-solar-beige rounded-xl p-4 text-center">
                  <div className="w-10 h-10 bg-[#0A2E1F] rounded-lg flex items-center justify-center mx-auto mb-2">
                    <i className="ri-flashlight-line text-[#FFB800]"></i>
                  </div>
                  <div className="text-xl font-bold text-gray-900">{selectedProject.power}</div>
                  <div className="text-xs text-gray-500">Potencia</div>
                </div>
                <div className="bg-[#FF6B35]/10 rounded-xl p-4 text-center">
                  <div className="w-10 h-10 bg-[#FF6B35] rounded-lg flex items-center justify-center mx-auto mb-2">
                    <i className="ri-money-euro-circle-line text-white"></i>
                  </div>
                  <div className="text-xl font-bold text-[#FF6B35]">{selectedProject.savingsYear.toLocaleString('es-ES')}€</div>
                  <div className="text-xs text-gray-500">Ahorro anual</div>
                </div>
                <div className="bg-solar-beige rounded-xl p-4 text-center">
                  <div className="w-10 h-10 bg-[#0A2E1F] rounded-lg flex items-center justify-center mx-auto mb-2">
                    <i className="ri-leaf-line text-[#FFB800]"></i>
                  </div>
                  <div className="text-xl font-bold text-gray-900">{selectedProject.co2Reduction}t</div>
                  <div className="text-xs text-gray-500">CO₂ evitado/año</div>
                </div>
              </div>

              {/* Savings Highlight */}
              <div className="bg-[#0A2E1F] rounded-2xl p-6 mb-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-[#FFB800] text-sm font-semibold mb-1">Reducción en factura eléctrica</div>
                    <div className="text-white text-4xl font-bold">{selectedProject.savingsPercent}%</div>
                  </div>
                  <div className="flex-1 min-w-[200px] ml-4">
                    <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#FFB800] to-[#FF6B35] rounded-full transition-all duration-1000"
                        style={{ width: `${selectedProject.savingsPercent}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-1.5">
                      <span>0%</span>
                      <span>Ahorro máximo</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Estimated ROI */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-[#FFB800]/20 rounded-lg flex items-center justify-center">
                      <i className="ri-timer-line text-[#FFB800]"></i>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">Retorno de inversión</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {Math.round((selectedProject.panels * 280) / selectedProject.savingsYear)} años
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Periodo estimado de amortización</p>
                </div>
                <div className="border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <i className="ri-plant-line text-green-600"></i>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">Equivalente ecológico</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {Math.round(selectedProject.co2Reduction * 45)} árboles
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Equivalente en absorción de CO₂ anual</p>
                </div>
              </div>

              {/* CTA */}
              <a
                href="#presupuesto"
                onClick={() => setSelectedProject(null)}
                className="w-full bg-[#FF6B35] text-white py-3.5 rounded-lg text-sm font-semibold hover:bg-[#FFB800] hover:text-gray-900 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Quiero un proyecto similar
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
